<script setup lang="ts">
import type { Tone } from './types'

/**
 * A state chip. It carries document status (Draft, Submitted, Approved,
 * Posted, Reversed) and nothing else — a badge is not a label, a tag or a
 * decoration, because §11 reserves colour for meaning.
 */

withDefaults(
  defineProps<{
    tone?: Tone
    /** `solid` for the single most important state on a screen. */
    variant?: 'subtle' | 'solid' | 'outline'
    size?: 'sm' | 'md'
    /** Prefix a status dot; helps where hue alone is not distinguishable. */
    dot?: boolean
  }>(),
  { tone: 'neutral', variant: 'subtle', size: 'md' },
)
</script>

<template>
  <span class="e-badge" :class="[`e-badge--${tone}`, `e-badge--${variant}`, `e-badge--${size}`]">
    <span v-if="dot" class="e-badge__dot" aria-hidden="true" />
    <slot />
  </span>
</template>

<style>
.e-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--erpx-space-1);
  height: 20px;
  padding-inline: var(--erpx-space-2);
  border: 1px solid transparent;
  border-radius: var(--erpx-radius-sm);
  font-size: var(--erpx-text-xs);
  font-weight: var(--erpx-weight-medium);
  line-height: 1;
  white-space: nowrap;
}

.e-badge--sm {
  height: 18px;
  padding-inline: 5px;
  font-size: var(--erpx-text-2xs);
}

.e-badge__dot {
  width: 6px;
  height: 6px;
  background-color: currentColor;
  border-radius: var(--erpx-radius-full);
}

.e-badge--subtle.e-badge--neutral { background-color: var(--erpx-color-neutral-surface); color: var(--erpx-color-neutral-text); border-color: var(--erpx-color-neutral-border); }
.e-badge--subtle.e-badge--accent  { background-color: var(--erpx-color-accent-surface);  color: var(--erpx-color-accent-text);  border-color: var(--erpx-color-accent-border); }
.e-badge--subtle.e-badge--success { background-color: var(--erpx-color-success-surface); color: var(--erpx-color-success-text); border-color: var(--erpx-color-success-border); }
.e-badge--subtle.e-badge--warning { background-color: var(--erpx-color-warning-surface); color: var(--erpx-color-warning-text); border-color: var(--erpx-color-warning-border); }
.e-badge--subtle.e-badge--danger  { background-color: var(--erpx-color-danger-surface);  color: var(--erpx-color-danger-text);  border-color: var(--erpx-color-danger-border); }
.e-badge--subtle.e-badge--info    { background-color: var(--erpx-color-info-surface);    color: var(--erpx-color-info-text);    border-color: var(--erpx-color-info-border); }

.e-badge--solid.e-badge--neutral { background-color: var(--erpx-color-neutral-solid); color: var(--erpx-color-neutral-on-solid); }
.e-badge--solid.e-badge--accent  { background-color: var(--erpx-color-accent-solid);  color: var(--erpx-color-accent-on-solid); }
.e-badge--solid.e-badge--success { background-color: var(--erpx-color-success-solid); color: var(--erpx-color-success-on-solid); }
.e-badge--solid.e-badge--warning { background-color: var(--erpx-color-warning-solid); color: var(--erpx-color-warning-on-solid); }
.e-badge--solid.e-badge--danger  { background-color: var(--erpx-color-danger-solid);  color: var(--erpx-color-danger-on-solid); }
.e-badge--solid.e-badge--info    { background-color: var(--erpx-color-info-solid);    color: var(--erpx-color-info-on-solid); }

.e-badge--outline { background-color: transparent; }
.e-badge--outline.e-badge--neutral { color: var(--erpx-color-neutral-text); border-color: var(--erpx-color-neutral-border); }
.e-badge--outline.e-badge--accent  { color: var(--erpx-color-accent-text);  border-color: var(--erpx-color-accent-border); }
.e-badge--outline.e-badge--success { color: var(--erpx-color-success-text); border-color: var(--erpx-color-success-border); }
.e-badge--outline.e-badge--warning { color: var(--erpx-color-warning-text); border-color: var(--erpx-color-warning-border); }
.e-badge--outline.e-badge--danger  { color: var(--erpx-color-danger-text);  border-color: var(--erpx-color-danger-border); }
.e-badge--outline.e-badge--info    { color: var(--erpx-color-info-text);    border-color: var(--erpx-color-info-border); }
</style>
